/**
 * Liste des tickets de support
 * Design System v2 (Volvo-inspired) - Matching CreditNotes style
 */

import { useState } from 'react';
import { Search, Plus, RefreshCw, Ticket, MessageSquare, Headphones } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useTickets } from '../hooks/useTickets';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketPriorityBadge } from './TicketPriorityBadge';

interface TicketListProps {
  onSelectTicket?: (id: string) => void;
  onCreateTicket?: () => void;
}

export function TicketList({ onSelectTicket, onCreateTicket }: TicketListProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<{ search?: string; status?: string }>({});
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useTickets({
    ...filters,
    page,
    limit: 20,
  });

  if (error) {
    return (
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-8 text-center">
        <div className="text-red-500 mb-4">{t('support.loadError')}</div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 hover:shadow-lg"
        >
          {t('common.actions.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 flex items-center gap-3">
            <Headphones className="w-10 h-10 text-teal-600" />
            {t('support.title')}
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {t('support.subtitle')}
          </p>
        </div>
        <button
          onClick={onCreateTicket}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl font-medium
                     hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-200/50 hover:scale-[1.02]
                     active:scale-[0.98] transition-all duration-300 shadow-sm"
        >
          <Plus className="w-5 h-5" />
          {t('support.newTicket')}
        </button>
      </div>

      {/* Filters Card */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400
                                 group-focus-within:text-teal-500 transition-colors duration-300" />
              <input
                type="text"
                placeholder={t('support.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2.5 bg-white/40 border border-white/50 rounded-xl
                           focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                           hover:bg-white/60 hover:border-teal-200 transition-all duration-300"
                value={filters.search || ''}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            className="px-4 py-2.5 bg-white/40 border border-white/50 rounded-xl
                       focus:ring-2 focus:ring-teal-500 hover:bg-white/60 hover:border-teal-200
                       transition-all duration-300 cursor-pointer"
            value={filters.status || ''}
            onChange={(e) => {
              setFilters({ ...filters, status: e.target.value || undefined });
              setPage(1);
            }}
          >
            <option value="">{t('support.allStatuses')}</option>
            <option value="OPEN">{t('support.status.OPEN')}</option>
            <option value="IN_PROGRESS">{t('support.status.IN_PROGRESS')}</option>
            <option value="RESOLVED">{t('support.status.RESOLVED')}</option>
            <option value="CLOSED">{t('support.status.CLOSED')}</option>
          </select>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-600
                       hover:bg-white hover:text-teal-600 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-100/50
                       active:scale-95 transition-all duration-300"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full" />
            <p className="text-gray-600 mt-4">{t('support.loadingTickets')}</p>
          </div>
        ) : !data?.tickets?.length ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Ticket className="w-8 h-8 text-teal-600" />
            </div>
            <p className="text-gray-900 font-medium text-lg">{t('support.noTickets')}</p>
            <p className="text-gray-500 mt-1">
              {t('support.noTicketsYet')}
            </p>
            <button
              onClick={onCreateTicket}
              className="mt-4 px-5 py-2.5 bg-teal-50 text-teal-700 rounded-xl font-medium
                         hover:bg-teal-100 hover:shadow-lg hover:shadow-teal-100/50 transition-all duration-300"
            >
              {t('support.createFirst')}
            </button>
          </div>
        ) : (
          <>
            {/* Tickets Cards */}
            <div className="divide-y divide-white/20">
              {data.tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="p-5 bg-white/10 hover:bg-white hover:shadow-lg hover:shadow-teal-100/30
                             cursor-pointer transition-all duration-300 group border-l-4 border-transparent
                             hover:border-l-teal-500"
                  onClick={() => onSelectTicket?.(ticket.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-500 font-mono bg-gray-100/80 px-2 py-0.5 rounded-lg">
                          #{ticket.number || ticket.id.slice(0, 8)}
                        </span>
                        <TicketStatusBadge status={ticket.status} size="sm" />
                        <TicketPriorityBadge priority={ticket.priority} size="sm" />
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors duration-300 truncate text-lg">
                        {ticket.subject}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {ticket.description}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(ticket.updatedAt || ticket.createdAt), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </div>
                      {(ticket._count?.messages ?? 0) > 0 && (
                        <div className="flex items-center justify-end gap-1 mt-2 text-gray-400 group-hover:text-teal-500 transition-colors">
                          <MessageSquare className="w-4 h-4" />
                          <span className="text-sm font-medium">{ticket._count?.messages}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-white/30 bg-gray-50/30">
                <p className="text-sm text-gray-700 font-medium">
                  {t('support.pagination.page')} <span className="font-bold">{page}</span> {t('support.pagination.of')}{' '}
                  <span className="font-bold">{data.pagination.totalPages}</span>
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium
                               disabled:opacity-50 disabled:cursor-not-allowed
                               hover:bg-gray-50 hover:border-gray-300 hover:shadow-md active:scale-95 transition-all duration-300"
                  >
                    {t('support.pagination.previous')}
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
                    disabled={page === data.pagination.totalPages}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium
                               disabled:opacity-50 disabled:cursor-not-allowed
                               hover:bg-gray-50 hover:border-gray-300 hover:shadow-md active:scale-95 transition-all duration-300"
                  >
                    {t('support.pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
